import React, { useState, useEffect } from 'react';
import '../styles/FolderAccessControl.css';
import io from 'socket.io-client';
import axios from 'axios';

// API base URL
const API_BASE_URL = 'https://gnrl-bknd-server.onrender.com';
let socket;

const FolderAccessControl = () => {
  const [selectedUser, setSelectedUser] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderStructure, setFolderStructure] = useState(null);
  const [folderAccess, setFolderAccess] = useState({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showActivity, setShowActivity] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(12);
  const [activeView, setActiveView] = useState('users'); // 'users' or 'folders'
  const adminUser = localStorage.getItem('adminUser') || 'admin';

  // Mock folder structure for demo
  const mockFolderStructure = {
    id: 'root',
    name: 'Course Content',
    path: 'root',
    subfolders: [
      {
        id: 'folder1',
        name: '1. Introduction',
        path: 'folder1',
        files: [{id: 'audio1', name: 'Welcome.mp3'}],
        subfolders: [
          {
            id: 'subfolder1',
            name: '1.1 Getting Started',
            path: 'subfolder1',
            files: [{id: 'audio2', name: 'Lesson1.mp3'}],
            subfolders: []
          }
        ]
      },
      {
        id: 'folder2',
        name: '2. Basic Concepts',
        path: 'folder2',
        files: [{id: 'audio3', name: 'Basics.mp3'}],
        subfolders: []
      },
      {
        id: 'folder3',
        name: '3. Advanced Topics',
        path: 'folder3',
        files: [{id: 'audio4', name: 'Advanced.mp3'}],
        subfolders: []
      }
    ]
  };

  useEffect(() => {
    // Initialize Socket.IO connection
    socket = io(API_BASE_URL);
    
    socket.on('connect', () => {
      console.log('Connected to Socket.IO server');
      setSocketConnected(true);
      
      // Join admin room
      socket.emit('join-admin-room');
    });
    
    socket.on('folder-access-updated', (data) => {
      // Add to recent updates
      setRecentUpdates(prev => {
        // Check if a similar update already exists (same user, folder, and action within last 2 seconds)
        const now = new Date();
        const isDuplicate = prev.some(update => 
          update.username === data.username && 
          update.folderId === data.folderId && 
          update.isLocked === data.isLocked &&
          (now - new Date(update.timestamp)) < 2000 // Within 2 seconds
        );
        
        if (isDuplicate) {
          return prev; // Don't add duplicate update
        }
        
        const newUpdates = [
          {
            id: Date.now(),
            username: data.username,
            folderId: data.folderId,
            isLocked: data.isLocked,
            unlockedBy: data.unlockedBy,
            timestamp: new Date()
          },
          ...prev
        ].slice(0, 5); // Keep only the 5 most recent updates
        
        // If this update is for the currently selected user, update the UI
        if (data.username === selectedUser) {
          setFolderAccess(prev => ({
            ...prev,
            [data.folderId]: {
              isLocked: data.isLocked,
              manuallyUnlocked: !data.isLocked,
              unlockedBy: data.unlockedBy,
              unlockedAt: data.unlockedAt
            }
          }));
        }
        
        return newUpdates;
      });
    });
    
    socket.on('error', (error) => {
      console.error('Socket error:', error);
      setError(`Socket error: ${error.message}`);
    });
    
    socket.on('disconnect', () => {
      console.log('Disconnected from Socket.IO server');
      setSocketConnected(false);
    });

    // Load folder structure and users
    setLoading(true);
    fetchFolderStructure();
    fetchUsers();

    // Clean up socket connection on component unmount
    return () => {
      if (socket) socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (selectedUser) {
      // Fetch folder access for selected user
      fetchUserFolderAccess(selectedUser);
      // Switch to folder view when a user is selected
      setActiveView('folders');
    }
  }, [selectedUser]);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm) {
      const filtered = allUsers.filter(user => {
        // Search in username, email, name, or fullName
        const searchLower = searchTerm.toLowerCase();
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const name = (user.name || '').toLowerCase();
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        
        return username.includes(searchLower) || 
               email.includes(searchLower) || 
               name.includes(searchLower) || 
               fullName.includes(searchLower);
      });
      setFilteredUsers(filtered);
      setCurrentPage(1); // Reset to first page when filtering
    } else {
      setFilteredUsers(allUsers);
    }
  }, [searchTerm, allUsers]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setError(null);
      
      // Direct fetch from the API using axios like in UserAccessControl
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      
      if (response.data && Array.isArray(response.data)) {
        // Successfully retrieved users
        setAllUsers(response.data);
        setFilteredUsers(response.data);
        console.log(`Successfully loaded ${response.data.length} users from database`);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again later.');
      
      // Try alternative endpoint as fallback
      try {
        const altResponse = await axios.get(`${API_BASE_URL}/api/students`);
        if (altResponse.data && Array.isArray(altResponse.data.students)) {
          setAllUsers(altResponse.data.students);
          setFilteredUsers(altResponse.data.students);
          console.log(`Successfully loaded ${altResponse.data.students.length} students as fallback`);
        } else {
          throw new Error('Invalid response from students API');
        }
      } catch (fallbackError) {
        console.error('All user fetch attempts failed:', fallbackError);
        // No mock users as fallback - just show empty state
        setAllUsers([]);
        setFilteredUsers([]);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchFolderStructure = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/folder-structure`);
      
      if (response.ok) {
        const data = await response.json();
        setFolderStructure(data);
        // Initialize root folder as expanded
        setExpandedFolders({ [data.id]: true });
      } else {
        console.log('Using mock folder structure');
        setFolderStructure(mockFolderStructure);
        // Initialize root folder as expanded
        setExpandedFolders({ 'root': true });
      }
    } catch (error) {
      console.error('Error fetching folder structure:', error);
      setFolderStructure(mockFolderStructure);
      // Initialize root folder as expanded
      setExpandedFolders({ 'root': true });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserFolderAccess = async (username) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/folder-access/${username}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert array to object with folderId as key
        const accessMap = {};
        if (data.folderAccess && Array.isArray(data.folderAccess)) {
          data.folderAccess.forEach(access => {
            accessMap[access.folderId] = access;
          });
        }
        setFolderAccess(accessMap);
      } else {
        // Default: First folder unlocked, others locked
        const initialAccess = {
          'folder1': { isLocked: false, manuallyUnlocked: true, unlockedBy: 'system', unlockedAt: new Date() },
          'folder2': { isLocked: true },
          'folder3': { isLocked: true }
        };
        setFolderAccess(initialAccess);
      }
    } catch (error) {
      console.error('Error fetching user folder access:', error);
      // Default: First folder unlocked, others locked
      const initialAccess = {
        'folder1': { isLocked: false, manuallyUnlocked: true, unlockedBy: 'system', unlockedAt: new Date() },
        'folder2': { isLocked: true },
        'folder3': { isLocked: true }
      };
      setFolderAccess(initialAccess);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    // Use user._id or username depending on what's available
    const userId = user._id || user.id;
    const username = user.username || user.email || user.name || userId;
    setSelectedUser(username);
  };

  // Manual refresh of user list
  const handleRefreshUsers = () => {
    fetchUsers();
  };

  const toggleFolderAccess = async (folder, enabled, e) => {
    if (e) e.stopPropagation(); // Prevent folder expansion when toggling access
    
    try {
      const updateData = {
        username: selectedUser,
        folderId: folder.id,
        isLocked: !enabled,
        unlockedBy: adminUser
      };
      
      // Use Socket.IO for real-time update if connected
      if (socketConnected) {
        socket.emit('folder-access-update', updateData);
      } else {
        // Fallback to REST API if socket is not connected
        try {
          const response = await fetch(`${API_BASE_URL}/api/folder-access/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData),
          });

          if (!response.ok) {
            throw new Error('Failed to update folder access');
          }
        } catch (error) {
          console.error('Error updating folder access via REST:', error);
        }
      }
      
      // Optimistically update UI
      setFolderAccess(prev => ({
        ...prev,
        [folder.id]: {
          isLocked: !enabled,
          manuallyUnlocked: enabled,
          unlockedBy: enabled ? adminUser : undefined,
          unlockedAt: enabled ? new Date() : undefined
        }
      }));
      
      // Add to recent updates locally - prevent duplicates
      setRecentUpdates(prev => {
        // Check if a similar update already exists (same user, folder, and action within last 2 seconds)
        const now = new Date();
        const isDuplicate = prev.some(update => 
          update.username === selectedUser && 
          update.folderId === folder.id && 
          update.isLocked === !enabled &&
          (now - new Date(update.timestamp)) < 2000 // Within 2 seconds
        );
        
        if (isDuplicate) {
          return prev; // Don't add duplicate update
        }
        
        const newUpdates = [
          {
            id: Date.now(),
            username: selectedUser,
            folderId: folder.id,
            isLocked: !enabled,
            unlockedBy: enabled ? adminUser : undefined,
            timestamp: new Date()
          },
          ...prev
        ].slice(0, 5);
        
        return newUpdates;
      });
    } catch (error) {
      console.error('Error toggling folder access:', error);
      setError('Failed to update folder access');
    }
  };

  const toggleFolderExpand = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const renderFolderTree = (folder, level = 0) => {
    if (!folder) return null;

    const access = folderAccess[folder.id];
    const isLocked = access ? access.isLocked : true;
    const unlockedBy = access?.unlockedBy;
    const unlockedAt = access?.unlockedAt ? new Date(access.unlockedAt).toLocaleString() : '';
    const isExpanded = expandedFolders[folder.id];
    const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
    const hasFiles = folder.files && folder.files.length > 0;
    
    return (
      <div className="folder-tree-item">
        <div 
          className={`folder-row ${isLocked ? 'locked' : 'unlocked'}`}
          onClick={() => (hasSubfolders || hasFiles) && toggleFolderExpand(folder.id)}
        >
          <div className="folder-info">
            {(hasSubfolders || hasFiles) && (
              <span className={`collapse-icon ${isExpanded ? 'expanded' : ''}`}>
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            <span className="folder-icon">
              {isLocked ? '🔒' : (isExpanded ? '📂' : '📁')}
            </span>
            <span className="folder-name">{folder.name}</span>
            {hasFiles && (
              <span className="file-count">{folder.files.length}</span>
            )}
          </div>
          
          {selectedUser && (
            <div className="folder-controls">
              <label className="switch" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={!isLocked}
                  onChange={(e) => toggleFolderAccess(folder, e.target.checked, e)}
                />
                <span className="slider round"></span>
              </label>
            </div>
          )}
        </div>
        
        {!isLocked && unlockedBy && (
          <div className="unlock-info">
            <span className="unlock-badge" title={`Unlocked at: ${unlockedAt}`}>
              Unlocked by {unlockedBy}
            </span>
          </div>
        )}
        
        {isExpanded && (
          <div className="folder-children">
            {hasSubfolders && folder.subfolders.map((subfolder, index) => (
              <div key={index} className="subfolder">
                {renderFolderTree(subfolder, level + 1)}
              </div>
            ))}
            
            {hasFiles && (
              <div className="files-list">
                {folder.files.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{file.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRecentUpdates = () => {
    if (recentUpdates.length === 0) {
      return (
        <div className="no-updates">
          <p>No recent activity</p>
        </div>
      );
    }
    
    return (
      <div className="updates-list-container">
        <ul className="updates-list">
          {recentUpdates.map(update => (
            <li key={update.id} className="update-item">
              <div className="update-header">
                <span className={`update-status ${update.isLocked ? 'locked' : 'unlocked'}`}>
                  {update.isLocked ? 'Locked' : 'Unlocked'}
                </span>
                <span className="update-time">{new Date(update.timestamp).toLocaleTimeString()}</span>
              </div>
              <div className="update-user">{update.username}</div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Generate avatar color based on username or email
  const generateAvatarColor = (user) => {
    if (!user) return 'hsl(210, 70%, 80%)';
    
    // Use whatever identifier is available
    const identifier = user.username || user.email || user.name || user._id || '';
    
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  // Get user initials for avatar
  const getUserInitials = (user) => {
    if (!user) return '?';
    
    // Try to use first and last name if available
    if (user.firstName && user.lastName) {
      return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }
    
    // Try to use full name if available
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length > 1) {
        return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    
    // Handle email
    if (user.email) {
      if (user.email.includes('@')) {
        const emailName = user.email.split('@')[0];
        if (emailName.includes('.')) {
          const parts = emailName.split('.');
          return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
        }
        return emailName.substring(0, 2).toUpperCase();
      }
      return user.email.substring(0, 2).toUpperCase();
    }
    
    // Use username if available
    if (user.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    
    // Last resort: use ID
    return user._id ? user._id.substring(0, 2).toUpperCase() : '??';
  };

  // Get display name for user
  const getDisplayName = (user) => {
    if (!user) return '';
    
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    if (user.name) {
      return user.name;
    }
    
    if (user.username) {
      return user.username;
    }
    
    if (user.email) {
      return user.email;
    }
    
    return user._id || 'Unknown User';
  };

  // Handle switching between views
  const switchToUserView = () => {
    setActiveView('users');
  };

  const switchToFolderView = () => {
    if (selectedUser) {
      setActiveView('folders');
    } else {
      // If no user is selected, show an error or prompt
      setError('Please select a user first to manage folder access');
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <div className="folder-access-container">
      <div className="folder-access-header">
        <div className="header-left">
          <h2>Folder Access Control</h2>
          <div className={`connection-status ${socketConnected ? 'connected' : 'disconnected'}`}>
            <span className="status-indicator"></span>
            <span className="status-text">
              {socketConnected ? 'Connected' : 'Offline'}
            </span>
          </div>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            <button 
              className={`view-toggle-btn ${activeView === 'users' ? 'active' : ''}`}
              onClick={switchToUserView}
            >
              <span className="view-icon">👥</span>
              <span className="view-text">Users</span>
            </button>
            <button 
              className={`view-toggle-btn ${activeView === 'folders' ? 'active' : ''} ${!selectedUser ? 'disabled' : ''}`}
              onClick={switchToFolderView}
              disabled={!selectedUser}
            >
              <span className="view-icon">📁</span>
              <span className="view-text">Folders</span>
            </button>
          </div>
          <button 
            className={`activity-toggle ${showActivity ? 'active' : ''}`} 
            onClick={() => setShowActivity(!showActivity)}
            title="Toggle recent activity"
          >
            <span className="activity-icon">🔔</span>
            <span className="activity-badge">{recentUpdates.length}</span>
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="folder-access-content">
        <div className="main-panel">
          {/* User Selection View */}
          {activeView === 'users' && (
            <div className="user-list-section">
              <div className="user-list-header">
                <h3>Select a User</h3>
                <div className="user-search">
                  <div className="search-input-wrapper">
                    <i className="search-icon">🔍</i>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="user-search-input"
                    />
                    {searchTerm && (
                      <button 
                        className="clear-search" 
                        onClick={() => setSearchTerm('')}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="user-count">
                    {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                    <button 
                      className="refresh-users-btn"
                      onClick={handleRefreshUsers}
                      disabled={usersLoading}
                      title="Refresh user list"
                    >
                      <span className={`refresh-icon ${usersLoading ? 'loading' : ''}`}>⟳</span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="users-list">
                {usersLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <span>Loading users from database...</span>
                  </div>
                ) : (
                  <>
                    {filteredUsers.length > 0 ? (
                      <div className="user-grid">
                        {currentUsers.map(user => (
                          <div 
                            key={user._id || user.id || user.email || user.username}
                            className={`user-card ${selectedUser === (user.username || user.email || user.name) ? 'selected' : ''}`}
                            onClick={() => handleUserSelect(user)}
                          >
                            <div 
                              className="user-avatar" 
                              style={{ backgroundColor: generateAvatarColor(user) }}
                            >
                              {getUserInitials(user)}
                            </div>
                            <div className="user-name">{getDisplayName(user)}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-users-message">
                        <div className="empty-state">
                          <div className="empty-icon">🔍</div>
                          <p>No users match your search</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button 
                          onClick={() => paginate(1)} 
                          disabled={currentPage === 1}
                          className="pagination-button first"
                          title="First page"
                        >
                          «
                        </button>
                        <button 
                          onClick={() => paginate(currentPage - 1)} 
                          disabled={currentPage === 1}
                          className="pagination-button prev"
                          title="Previous page"
                        >
                          ‹
                        </button>
                        
                        <div className="pagination-info">
                          Page {currentPage} of {totalPages}
                        </div>
                        
                        <button 
                          onClick={() => paginate(currentPage + 1)} 
                          disabled={currentPage === totalPages}
                          className="pagination-button next"
                          title="Next page"
                        >
                          ›
                        </button>
                        <button 
                          onClick={() => paginate(totalPages)} 
                          disabled={currentPage === totalPages}
                          className="pagination-button last"
                          title="Last page"
                        >
                          »
                        </button>
                      </div>
                    )}
                    
                    {selectedUser && (
                      <div className="selected-user-footer">
                        <div className="selected-user-info">
                          <div 
                            className="user-avatar selected-user-avatar" 
                            style={{ backgroundColor: generateAvatarColor({username: selectedUser}) }}
                          >
                            {getUserInitials({username: selectedUser})}
                          </div>
                          <span>Selected: <strong>{selectedUser}</strong></span>
                        </div>
                        <button 
                          className="manage-access-btn"
                          onClick={switchToFolderView}
                        >
                          Manage Folder Access →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Folder Access View */}
          {activeView === 'folders' && (
            <div className="folder-access-panel">
              <div className="folder-access-header-bar">
                <div className="selected-user-display">
                  <div 
                    className="user-avatar selected-user-avatar" 
                    style={{ backgroundColor: generateAvatarColor({username: selectedUser}) }}
                  >
                    {getUserInitials({username: selectedUser})}
                  </div>
                  <span>Managing access for <strong>{selectedUser}</strong></span>
                </div>
                <button 
                  className="change-user-btn"
                  onClick={switchToUserView}
                >
                  ← Change User
                </button>
              </div>
              
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <span>Loading folder structure...</span>
                </div>
              ) : (
                <div className="folder-tree-container">
                  <div className="folder-access-instructions">
                    <p>Toggle switches to lock or unlock folders for this user</p>
                  </div>
                  <div className="folder-tree">
                    {folderStructure && renderFolderTree(folderStructure)}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Activity panel as a collapsible section */}
          {showActivity && (
            <div className="inline-activity-panel">
              <div className="activity-header">
                <h3>Recent Activity</h3>
              </div>
              {renderRecentUpdates()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderAccessControl;
